import { useEffect, useMemo, useRef, useState } from "react";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HyperionToolTip from "../HyperionToolTip";

const AI_REVIEW_INTRO_START_DELAY = 180;
const AI_REVIEW_INTRO_STEP_INTERVAL = 320;
const AI_REVIEW_INTRO_METRICS_DELAY = 220;
const AI_REVIEW_INTRO_STEPS = 4;
const AI_REVIEW_INTRO_TOTAL_MS =
  AI_REVIEW_INTRO_START_DELAY +
  AI_REVIEW_INTRO_STEPS * AI_REVIEW_INTRO_STEP_INTERVAL +
  AI_REVIEW_INTRO_METRICS_DELAY;

const polar = (cx, cy, r, deg) => {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const buildArc = (cx, cy, r, startDeg, sweepDeg) => {
  if (sweepDeg <= 0) return "";
  const sweep = Math.min(sweepDeg, 359.99);
  const endDeg = (startDeg + sweep) % 360;
  const { x: sx, y: sy } = polar(cx, cy, r, startDeg);
  const { x: ex, y: ey } = polar(cx, cy, r, endDeg);
  return `M${sx.toFixed(3)},${sy.toFixed(3)} A${r},${r} 0 ${sweep > 180 ? 1 : 0},1 ${ex.toFixed(3)},${ey.toFixed(3)}`;
};

const useAnimatedOnce = (target, duration = 650, delay = 0) => {
  const [val, setVal] = useState(0);
  const raf = useRef();
  const t0 = useRef();

  useEffect(() => {
    t0.current = null;
    cancelAnimationFrame(raf.current);

    const startTimer = setTimeout(() => {
      const step = (ts) => {
        if (!t0.current) t0.current = ts;
        const t = Math.min((ts - t0.current) / duration, 1);
        setVal(target * (1 - Math.pow(1 - t, 3)));
        if (t < 1) raf.current = requestAnimationFrame(step);
      };
      raf.current = requestAnimationFrame(step);
    }, delay);

    return () => {
      clearTimeout(startTimer);
      cancelAnimationFrame(raf.current);
    };
  }, [target, duration, delay]);

  return val;
};

function TooltipPanel({ title, body, accent, T }) {
  return (
    <div
      style={{
        minWidth: 220,
        maxWidth: 300,
        borderRadius: 12,
        border: `1px solid ${(accent || T.brandLight)}44`,
        background: "linear-gradient(180deg, rgba(8,14,30,0.98) 0%, rgba(6,10,24,0.98) 100%)",
        boxShadow: "0 14px 28px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.04)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 10px 6px",
          borderBottom: `1px solid ${(accent || T.brandLight)}22`,
          background: `linear-gradient(180deg, ${(accent || T.brandLight)}14 0%, rgba(255,255,255,0.01) 100%)`,
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: "0.14em",
          color: accent || T.brandLight,
          textTransform: "uppercase",
          fontFamily: T.fontFamily,
        }}
      >
        {title}
      </div>
      <div
        style={{
          padding: "9px 10px 10px",
          fontSize: 11,
          lineHeight: 1.5,
          color: "#D6E4F7",
          fontFamily: T.fontFamily,
        }}
      >
        {body}
      </div>
    </div>
  );
}

function ArcGauge({ value, max = 100, label, size = 138, T, entranceDelay = 0 }) {
  const animVal = useAnimatedOnce(value, 700, entranceDelay);
  const pct = Math.min(Math.max(animVal / max, 0), 1);
  const color = T.gaugeColor(pct);
  const uid = `airp-g-${label.replace(/\W/g, "")}`;

  const CX = 60;
  const CY = 60;
  const R = 43;
  const START = 135;
  const SWEEP = 270;

  const trackPath = buildArc(CX, CY, R, START, SWEEP);
  const fillPath = buildArc(CX, CY, R, START, pct * SWEEP);
  const tipDeg = (START + pct * SWEEP) % 360;
  const tip = polar(CX, CY, R, tipDeg);

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    inner: polar(CX, CY, R - 7, START + t * SWEEP),
    outer: polar(CX, CY, R - 1, START + t * SWEEP),
    major: t === 0 || t === 0.5 || t === 1,
  }));

  const gauge = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        animation: `airp-scaleIn 0.24s ${entranceDelay}ms ease-out both`,
        minWidth: 116,
      }}
    >
      <svg width={size} height={Math.round(size * 0.9)} viewBox="0 0 120 108">
        <defs>
          <filter id={`${uid}-glow`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="4.2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id={`${uid}-face`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={color} stopOpacity="0.08" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </radialGradient>
        </defs>

        <circle cx={CX} cy={CY} r={R - 2} fill={`url(#${uid}-face)`} />
        <path d={trackPath} fill="none" stroke={T.surface2} strokeWidth={11} strokeLinecap="round" />

        {ticks.map((t, i) => (
          <line
            key={i}
            x1={t.inner.x.toFixed(2)}
            y1={t.inner.y.toFixed(2)}
            x2={t.outer.x.toFixed(2)}
            y2={t.outer.y.toFixed(2)}
            stroke={t.major ? T.borderMedium : T.borderSubtle}
            strokeWidth={t.major ? 1.45 : 1}
            strokeLinecap="round"
          />
        ))}

        {pct > 0.01 && (
          <path
            d={fillPath}
            fill="none"
            stroke={color}
            strokeWidth={11}
            strokeLinecap="round"
            filter={`url(#${uid}-glow)`}
            style={{ transition: "stroke 0.35s ease" }}
          />
        )}

        {pct > 0.03 && pct < 0.985 && (
          <circle cx={tip.x.toFixed(2)} cy={tip.y.toFixed(2)} r={5} fill={color} filter={`url(#${uid}-glow)`} />
        )}

        <circle cx={CX} cy={CY} r={R - 14} fill="none" stroke={T.borderSubtle} strokeWidth={1} />

        <text x={CX} y={CY + 5} textAnchor="middle" fill={T.textPrimary} fontSize={22} fontWeight={800} fontFamily={T.fontFamily} letterSpacing="-0.5" style={{ userSelect: "none" }}>
          {Math.round(animVal)}%
        </text>
        <text x={CX} y={CY + 18} textAnchor="middle" fill={T.textMuted} fontSize={7} fontFamily={T.fontFamily} style={{ userSelect: "none" }}>
          {max === 100 ? "" : `/ ${max}`}
        </text>
      </svg>

      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.18em",
          color: T.textSecondary,
          textTransform: "uppercase",
          marginTop: -2,
          fontFamily: T.fontFamily,
          lineHeight: 1,
        }}
      >
        {label}
      </div>
    </div>
  );

  const tooltipBody =
    label === "Confidence"
      ? "Confidence reflects how certain the AI is that its analysis and routing advice are reliable for this submission."
      : "Quality reflects the AI's overall assessment of the submission's strength based on structure, clarity, and completeness.";

  return (
    <HyperionToolTip
      variant="dark"
      rich
      placement="top"
      title={<TooltipPanel title={label} body={tooltipBody} accent={label === "Confidence" ? T.brandLight : T.review} T={T} />}
    >
      <div style={{ cursor: "help" }}>{gauge}</div>
    </HyperionToolTip>
  );
}

function AiCompletionStrip({ T, confNumeric, qualNumeric, onSequenceComplete }) {
  const steps = [
    {
      key: "data",
      Icon: StorageRoundedIcon,
      label: "Analysing submission",
      tooltipTitle: "Submission analysis",
      tooltipBody: "Uses the analysis payload for structure, comments, classification, and presentation signals.",
    },
    {
      key: "route",
      Icon: PersonSearchRoundedIcon,
      label: "Assessing reviewers",
      tooltipTitle: "Reviewer matching",
      tooltipBody: "Checks reviewers against the configured assignment fields and rules for this project.",
    },
    {
      key: "ai",
      Icon: AutoAwesomeRoundedIcon,
      label: "AI Magic",
      tooltipTitle: "AI recommendation layer",
      tooltipBody: "Combines the analysis result with reviewer matching signals to rank the suggested reviewers.",
    },
    {
      key: "done",
      Icon: CheckCircleRoundedIcon,
      complete: true,
      tooltipTitle: "Recommendation ready",
      tooltipBody: "The panel has finished combining the analysis and assignment signals for display.",
    },
  ];

  const [activeStep, setActiveStep] = useState(-1);
  const [showMetrics, setShowMetrics] = useState(false);

  useEffect(() => {
    const timers = [];

    setActiveStep(-1);
    setShowMetrics(false);
    onSequenceComplete?.(false);

    steps.forEach((_, index) => {
      timers.push(window.setTimeout(() => setActiveStep(index), AI_REVIEW_INTRO_START_DELAY + index * AI_REVIEW_INTRO_STEP_INTERVAL));
    });

    timers.push(window.setTimeout(() => {
      setShowMetrics(true);
      onSequenceComplete?.(true);
    }, AI_REVIEW_INTRO_TOTAL_MS));

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      onSequenceComplete?.(false);
    };
  }, [onSequenceComplete]);

  const statusLines = [
    activeStep >= 0 ? "Analysing submission" : "",
    activeStep >= 1 ? "Assessing reviewers" : "",
    activeStep >= 2 ? "AI Magic" : "",
  ];

  return (
    <div
      style={{
        position: "relative",
        minHeight: 122,
        padding: "10px 14px",
        borderRadius: 14,
        border: `1.5px solid ${T.brandLight}4D`,
        background: `linear-gradient(180deg, ${T.surface1} 0%, ${T.panelBg} 100%)`,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
        animation: "airp-slideDown 0.22s ease-out both",
        display: "grid",
        gridTemplateColumns: "172px 1fr auto",
        alignItems: "center",
        columnGap: 8,
      }}
    >
      <div
        style={{
          minWidth: 0,
          width: 172,
          height: 114,
          padding: "0 9px 9px",
          borderRadius: 12,
          border: `1px solid ${T.accepted}3A`,
          background: "linear-gradient(180deg, rgba(2,8,12,0.92) 0%, rgba(4,14,18,0.98) 100%)",
          boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), inset 0 0 20px rgba(55,255,140,0.06)`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          zIndex: 1,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.2em",
            color: T.accepted,
            textTransform: "uppercase",
            fontFamily: "'Consolas', 'Courier New', monospace",
            padding: "9px 11px 7px",
            borderBottom: "1px solid rgba(120,255,180,0.18)",
            textShadow: "0 0 12px rgba(120,255,180,0.24)",
            lineHeight: 1,
            background: "linear-gradient(180deg, rgba(20,56,34,0.2) 0%, rgba(20,56,34,0.04) 100%)",
          }}
        >
          To Do
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            gap: 8,
            padding: "12px 2px 16px 4px",
          }}
        >
        {statusLines.map((line, index) => {
          const visible = Boolean(line);
          const isCurrent = activeStep === index;

          return (
            <div
              key={`${index}-${line || "blank"}`}
              style={{
                minHeight: 16,
                display: "flex",
                alignItems: "center",
                gap: 5,
                opacity: visible ? 1 : 0.16,
                transition: "opacity 160ms ease",
                transform: visible ? "translateY(0)" : "translateY(2px)",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: visible ? T.accepted : `${T.accepted}22`,
                  boxShadow: visible ? `0 0 10px ${T.accepted}90` : "none",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.01em",
                  color: visible ? T.accepted : T.textMuted,
                  fontFamily: "'Consolas', 'Courier New', monospace",
                  textShadow: visible ? "0 0 10px rgba(120,255,180,0.2)" : "none",
                  whiteSpace: "normal",
                  lineHeight: 1.2,
                  flex: 1,
                }}
              >
                {line ? `${line}...` : "\u00A0"}
              </span>
              {isCurrent && (
                <span
                  style={{
                    width: 6,
                    height: 12,
                    borderRadius: 2,
                    background: T.accepted,
                    boxShadow: "0 0 10px rgba(120,255,180,0.45)",
                    animation: "airp-fadeIn 0.35s ease-out both",
                    flexShrink: 0,
                  }}
                />
              )}
            </div>
          );
        })}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifySelf: "center", width: 194, flexShrink: 0, zIndex: 1, marginTop: -2 }}>
        <div style={{ position: "relative", width: 188, height: 52, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", left: 18, right: 18, top: "50%", height: 2, transform: "translateY(-50%)", borderRadius: 999, background: `linear-gradient(90deg, ${T.borderSubtle} 0%, ${T.borderMedium} 50%, ${T.borderSubtle} 100%)`, opacity: 1 }} />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: 18,
              width: `${activeStep < 0 ? 0 : Math.min(138, (activeStep + 0.55) * 40)}px`,
              height: 4,
              transform: "translateY(-50%)",
              borderRadius: 999,
              background: `linear-gradient(90deg, ${T.brand} 0%, ${T.brandLight} 100%)`,
              boxShadow: `0 0 16px ${T.brandLight}30`,
              transition: "width 160ms ease-out",
            }}
          />
          <div style={{ width: 168, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0, position: "relative", zIndex: 1 }}>
            {steps.map(({ key, Icon, complete, tooltipTitle, tooltipBody }, index) => {
              const isActive = activeStep >= index;
              const isFinal = complete;

              return (
                <HyperionToolTip
                  key={key}
                  variant="dark"
                  rich
                  placement="top"
                  title={<TooltipPanel title={tooltipTitle} body={tooltipBody} accent={isFinal ? T.review : T.brandLight} T={T} />}
                >
                  <div
                    style={{
                      width: isFinal ? 40 : 33,
                      height: isFinal ? 40 : 33,
                      borderRadius: "50%",
                      border: `1px solid ${isActive ? (isFinal ? `${T.brandLight}77` : `${T.brandLight}55`) : T.borderMedium}`,
                      background: isActive
                        ? (isFinal
                          ? `linear-gradient(135deg, ${T.brandLight} 0%, ${T.review} 100%)`
                          : `linear-gradient(180deg, ${T.brand} 0%, ${T.brandLight} 100%)`)
                        : `linear-gradient(180deg, ${T.surface2} 0%, ${T.panelBg} 100%)`,
                      color: isActive ? "white" : T.textMuted,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: isActive
                        ? (isFinal ? `0 0 22px ${T.review}30` : `0 0 14px ${T.brandLight}22`)
                        : "none",
                      animation: isFinal && isActive
                        ? "airp-final-badge 0.34s ease-out both, airp-final-pulse 1.5s 0.34s ease-out"
                        : isActive
                          ? "airp-pipeline-node-bright 0.18s ease-out both"
                          : "none",
                      flexShrink: 0,
                      transition: "background 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease",
                      cursor: "help",
                    }}
                  >
                    <Icon sx={{ fontSize: isFinal ? 21 : 17 }} />
                  </div>
                </HyperionToolTip>
              );
            })}
          </div>
        </div>
        <div
          style={{
            marginTop: 10,
            minHeight: 18,
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.14em",
            color: T.textPrimary,
            textTransform: "uppercase",
            fontFamily: T.fontFamily,
            opacity: activeStep >= steps.length - 1 ? 1 : 0,
            transform: activeStep >= steps.length - 1 ? "translateY(0)" : "translateY(4px)",
            transition: "opacity 180ms ease, transform 180ms ease",
            textShadow: `0 0 16px ${T.brandLight}25`,
          }}
        >
          CHECK COMPLETE
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 2,
          width: 234,
          flexShrink: 0,
          zIndex: 1,
          opacity: 1,
          pointerEvents: "none",
          justifySelf: "end",
        }}
      >
        <ArcGauge value={showMetrics ? confNumeric : 0} max={100} label="Confidence" T={T} entranceDelay={0} size={118} />
        <ArcGauge value={showMetrics ? qualNumeric : 0} max={100} label="Quality" T={T} entranceDelay={120} size={118} />
      </div>
    </div>
  );
}

function SectionHead({ children, T }) {
  return (
    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", color: T.textSecondary, textTransform: "uppercase", marginBottom: 8, fontFamily: T.fontFamily }}>
      {children}
    </div>
  );
}

function signalToNumeric(sig) {
  const explicit = Number(sig?.score ?? sig?.value);
  if (Number.isFinite(explicit)) return Math.max(0.2, Math.min(explicit, 1));
  const conf = String(sig?.confidence || "").toUpperCase();
  if (conf.includes("HIGH")) return 0.9;
  if (conf.includes("MED")) return 0.65;
  if (conf.includes("LOW")) return 0.4;
  const rating = String(sig?.rating || "").toUpperCase();
  if (rating === "HIGH") return 0.88;
  if (rating === "MODERATE") return 0.62;
  if (rating === "LOW") return 0.34;
  return 0.45;
}

function MultiAxisRadar({ items, T }) {
  const normalizedItems = (items || []).slice(0, 5).map((sig) => {
    const rating = String(sig?.rating || sig?.label || "").toUpperCase();
    const tone = rating === "HIGH"
      ? T.accepted
      : rating === "MODERATE"
        ? T.warning
        : rating === "LOW"
          ? T.rejected
          : T.brandLight;
    return {
      key: sig.key || sig.label,
      label: sig.label || (sig.key || "").replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim(),
      value: sig.value != null ? Number(sig.value) : signalToNumeric(sig),
      tone,
      rating: sig.rating || "-",
      confidence: sig.confidence || sig.helper || "-",
    };
  });

  if (!normalizedItems.length) return null;

  const cx = 270;
  const cy = 128;
  const radius = 82;
  const rings = [0.25, 0.5, 0.75, 1];
  const angleStep = (Math.PI * 2) / normalizedItems.length;
  const angleStart = -Math.PI / 2;

  const polar = (r, i) => {
    const a = angleStart + i * angleStep;
    return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r };
  };

  const polygonPath = normalizedItems
    .map((item, i) => {
      const p = polar(radius * item.value, i);
      return `${i === 0 ? "M" : "L"}${p.x.toFixed(2)},${p.y.toFixed(2)}`;
    })
    .join(" ") + " Z";

  return (
    <div style={{ display: "grid", gridTemplateColumns: "392px minmax(0, 468px)", gap: 18, alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ position: "relative", width: 392, height: 244, paddingLeft: 10, paddingRight: 10, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <svg width="368" height="244" viewBox="0 0 392 256">
          <defs>
            <linearGradient id="airp-radar-fill" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={T.brandLight} stopOpacity="0.30" />
              <stop offset="100%" stopColor={T.review} stopOpacity="0.14" />
            </linearGradient>
          </defs>

          <circle cx={cx} cy={cy} r={radius + 12} fill="rgba(255,255,255,0.01)" />

          {rings.map((ring) => {
            const pts = normalizedItems.map((_, i) => {
              const p = polar(radius * ring, i);
              return `${p.x.toFixed(2)},${p.y.toFixed(2)}`;
            }).join(" ");
            return <polygon key={ring} points={pts} fill="none" stroke={T.borderSubtle} strokeWidth="1" />;
          })}

          {normalizedItems.map((_, i) => {
            const p = polar(radius, i);
            const labelPoint = polar(radius + 26, i);
            return (
              <g key={`axis-${i}`}>
                <line x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={T.borderSubtle} strokeWidth="1" />
                <text
                  x={labelPoint.x}
                  y={labelPoint.y}
                  textAnchor={Math.abs(labelPoint.x - cx) < 10 ? "middle" : labelPoint.x < cx ? "end" : "start"}
                  dominantBaseline="middle"
                  fill={T.textSecondary}
                  fontSize="8.8"
                  fontWeight="700"
                  fontFamily={T.fontFamily}
                  style={{ userSelect: "none", letterSpacing: "0.02em" }}
                >
                  {normalizedItems[i].label}
                </text>
              </g>
            );
          })}

          <path d={polygonPath} fill="url(#airp-radar-fill)" stroke={T.brandLight} strokeWidth="2" />

          {normalizedItems.map((item, i) => {
            const p = polar(radius * item.value, i);
            return (
              <g key={item.key}>
                <circle cx={p.x} cy={p.y} r="5" fill={item.tone} />
                <circle cx={p.x} cy={p.y} r="11" fill={item.tone} opacity="0.12" />
              </g>
            );
          })}
        </svg>
      </div>

      <EvidenceConsole items={normalizedItems} T={T} />
    </div>
  );
}

function SectionShell({ T, children }) {
  return (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: 14,
        border: `1.5px solid ${T.borderMedium}`,
        background: `linear-gradient(180deg, ${T.surface1} 0%, rgba(7,13,30,0.96) 100%)`,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
      }}
    >
      {children}
    </div>
  );
}

function EvidenceConsole({ items, T }) {
  if (!items?.length) return null;

  return (
    <div
      style={{
        height: 204,
        borderRadius: 14,
        border: `1px solid ${T.borderMedium}`,
        background: `linear-gradient(180deg, rgba(12,22,46,0.98) 0%, rgba(8,16,36,0.98) 100%)`,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), inset 0 -18px 32px rgba(0,0,0,0.14)",
        padding: "10px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {items.map((item) => {
        const tone =
          item.rating === "HIGH"
            ? T.accepted
            : item.rating === "MODERATE"
              ? T.warning
              : item.rating === "LOW"
                ? T.rejected
                : T.brandLight;

        return (
          <div
            key={item.key}
            style={{
              minWidth: 0,
              flex: 1,
              display: "grid",
              gridTemplateColumns: "108px minmax(0, 1fr) 68px",
              gap: 10,
              alignItems: "center",
              padding: "6px 9px",
              borderRadius: 10,
              background: "linear-gradient(180deg, rgba(255,255,255,0.015) 0%, rgba(255,255,255,0.005) 100%)",
              border: `1px solid ${T.borderSubtle}`,
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.02)",
            }}
            title={`${item.label}: ${item.helper} (${item.rating})`}
          >
              <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: tone, boxShadow: `0 0 10px ${tone}55`, flexShrink: 0 }} />
                <span style={{ fontSize: 9, fontWeight: 700, color: T.textPrimary, fontFamily: T.fontFamily, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {item.label}
                </span>
              </div>
              <div style={{ paddingLeft: 15, fontSize: 14, fontWeight: 800, color: tone, lineHeight: 1, fontFamily: T.fontFamily, whiteSpace: "nowrap" }}>
                {item.helper}
              </div>
            </div>

            <div style={{ minWidth: 0, position: "relative", height: 14, display: "flex", alignItems: "center" }}>
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  height: 2,
                  borderRadius: 999,
                  background: `linear-gradient(90deg, ${T.borderSubtle} 0%, ${T.surface2} 100%)`,
                }}
              />
              {[0.2, 0.4, 0.6, 0.8].map((tick) => (
                <div
                  key={tick}
                  style={{
                    position: "absolute",
                    left: `${tick * 100}%`,
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 1,
                    height: 7,
                    background: T.borderSubtle,
                    opacity: 0.9,
                  }}
                />
              ))}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: `${Math.max(0, Math.min(100, Math.round(item.value * 100)))}%`,
                  height: 2,
                  borderRadius: 999,
                  background: tone,
                  boxShadow: `0 0 12px ${tone}44`,
                }}
              />
              <div
                style={{
                  position: "absolute",
                  left: `${Math.max(0, Math.min(100, Math.round(item.value * 100)))}%`,
                  top: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: `radial-gradient(circle at 35% 35%, rgba(255,255,255,0.35) 0%, ${tone} 38%, ${tone} 100%)`,
                  boxShadow: `0 0 12px ${tone}66, inset 0 1px 0 rgba(255,255,255,0.24)`,
                  border: `1px solid ${tone}AA`,
                }}
              />
            </div>

            <div style={{ minWidth: 68, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
              <div style={{ fontSize: 6.5, fontWeight: 800, color: T.textMuted, fontFamily: T.fontFamily, letterSpacing: "0.12em", textTransform: "uppercase", lineHeight: 1 }}>
                Status
              </div>
              <div style={{ fontSize: 9, fontWeight: 800, color: tone, fontFamily: T.fontFamily, letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1, whiteSpace: "nowrap" }}>
                {item.rating}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function AiReviewPopupDeepScanSection({
  T,
  score,
  specialist,
  structureScore,
  titleAlignment,
  sectionBreakdown,
  vaguenessFlags,
  structuralSuggestions,
  recommendedTopic,
  topicAmbiguity,
  recommendedSpeciality,
  presentationStrength,
  presentationSummary,
  currentSubmissionReviewCount,
}) {
  const flagCount = vaguenessFlags?.flaggedSentences?.length ?? 0;
  const balanceScore = sectionBreakdown?.balance;
  const coverageScore = sectionBreakdown?.coverage;
  const specificityScore = vaguenessFlags?.score;
  const recommendedTopicName = recommendedTopic?.name || "";
  const recommendedSpecialityName = recommendedSpeciality?.name || "";
  const topicAmbiguityDetected = topicAmbiguity?.detected === true;
  const topicAmbiguityMessage = topicAmbiguity?.message || "";

  const formatPct = (v) => {
    if (v == null) return "-";
    const n = Number(v);
    return Number.isFinite(n) ? `${Math.round(Math.min(Math.max(n, 0), 1) * 100)}%` : "-";
  };

  const evidenceAxes = [
    { key: "structure", label: "Structure", value: structureScore ?? 0, rating: structureScore >= 0.8 ? "HIGH" : structureScore >= 0.55 ? "MODERATE" : "LOW", helper: formatPct(structureScore) },
    { key: "coverage", label: "Coverage", value: coverageScore ?? 0, rating: coverageScore >= 0.8 ? "HIGH" : coverageScore >= 0.55 ? "MODERATE" : "LOW", helper: formatPct(coverageScore) },
    { key: "balance", label: "Balance", value: balanceScore ?? 0, rating: balanceScore >= 0.8 ? "HIGH" : balanceScore >= 0.55 ? "MODERATE" : "LOW", helper: formatPct(balanceScore) },
    { key: "title", label: "Title fit", value: titleAlignment ?? 0, rating: titleAlignment >= 0.7 ? "HIGH" : titleAlignment >= 0.5 ? "MODERATE" : "LOW", helper: formatPct(titleAlignment) },
    { key: "specificity", label: "Specificity", value: specificityScore ?? 0, rating: specificityScore >= 0.85 ? "HIGH" : specificityScore >= 0.65 ? "MODERATE" : "LOW", helper: formatPct(specificityScore) },
  ].filter((item) => item.value != null);

  const barSegments = useMemo(() => {
    const props = sectionBreakdown?.proportions;
    if (!props || typeof props !== "object") return [];
    return ["background", "methods", "results", "conclusions"]
      .map((k) => ({ key: k, value: Number(props[k]) }))
      .filter((s) => Number.isFinite(s.value) && s.value > 0);
  }, [sectionBreakdown]);

  const segColor = {
    background: "#F472B6",
    methods: "#2DD4BF",
    results: T.warning,
    conclusions: "#A78BFA",
  };

  const decisionPillData = [
    {
      label: "Quality score",
      value: score != null ? `${score}/5` : "-",
      tone: score >= 4 ? T.accepted : score >= 3 ? T.warning : T.rejected,
      detail: score >= 4 ? "Strong submission quality" : score >= 3 ? "Usable with some caution" : "Needs closer review",
    },
    {
      label: "Specialist input",
      value: specialist ? "Required" : "Not required",
      tone: specialist ? T.gold : T.brandLight,
      detail: specialist ? "Subject-matter review is warranted" : "Standard reviewer fit looks sufficient",
    },
    {
      label: "Specific flags",
      value: flagCount === 0 ? "None" : `${flagCount} flagged`,
      tone: flagCount <= 1 ? T.accepted : flagCount <= 3 ? T.warning : T.rejected,
      detail: flagCount === 0 ? "No major writing concerns detected" : "Check the flagged claim before final routing",
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, animation: "airp-fadeSlide 0.22s ease-out both" }}>
      <SectionShell T={T}>
        <SectionHead T={T}>Decision basis</SectionHead>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
          {decisionPillData.map((item) => (
            <div
              key={item.label}
              style={{
                minHeight: 86,
                padding: "10px 12px",
                borderRadius: 12,
                border: `1px solid ${item.tone}55`,
                background: `linear-gradient(180deg, rgba(8,15,36,0.96) 0%, rgba(10,21,45,0.98) 100%)`,
                boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 1px ${item.tone}14`,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.13em", color: T.textMuted, textTransform: "uppercase", fontFamily: T.fontFamily }}>
                {item.label}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: item.tone, lineHeight: 1, fontFamily: T.fontFamily }}>
                {item.value}
              </div>
              <div style={{ fontSize: 10, color: "#D4E3F5", lineHeight: 1.45, fontFamily: T.fontFamily }}>
                {item.detail}
              </div>
            </div>
          ))}
        </div>
      </SectionShell>

      <SectionShell T={T}>
        <SectionHead T={T}>Submission quality evidence</SectionHead>
        <MultiAxisRadar items={evidenceAxes} T={T} />

        {barSegments.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 9, color: "#D7E6FA", fontFamily: T.fontFamily, marginBottom: 4, fontWeight: 700, letterSpacing: "0.04em" }}>Section balance</div>
            <div style={{ display: "flex", borderRadius: 4, overflow: "hidden", height: 8, background: T.surface2, gap: 1 }}>
              {barSegments.map((s) => (
                <div
                  key={s.key}
                  style={{ width: `${Math.max(s.value * 100, 4)}%`, background: segColor[s.key] || T.textMuted, transition: "width 0.4s ease" }}
                  title={`${s.key}: ${Math.round(s.value * 100)}%`}
                />
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
              {barSegments.map((s) => (
                <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 7, height: 7, borderRadius: 2, background: segColor[s.key] || T.textMuted }} />
                  <span style={{ fontSize: 9, color: "#C9DCF3", fontFamily: T.fontFamily }}>
                    {s.key} {Math.round(s.value * 100)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {Array.isArray(structuralSuggestions) && structuralSuggestions.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
            {structuralSuggestions.slice(0, 3).map((s, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 8,
                  padding: "6px 10px",
                  background: T.surface1,
                  borderRadius: 6,
                  borderLeft: `2px solid ${T.brand}`,
                  fontSize: 10,
                  color: T.textSecondary,
                  lineHeight: 1.4,
                  fontFamily: T.fontFamily,
                }}
              >
                <span style={{ color: T.brand, fontWeight: 800, flexShrink: 0 }}>{i + 1}</span>
                {s}
              </div>
            ))}
          </div>
        )}
      </SectionShell>

      {(recommendedTopicName || recommendedSpecialityName || presentationStrength || topicAmbiguityDetected) && (
        <SectionShell T={T}>
          <SectionHead T={T}>AI classification</SectionHead>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
            {[
              { k: "Presentation", v: presentationStrength },
              { k: "Topic", v: recommendedTopicName },
              { k: "Speciality", v: recommendedSpecialityName },
            ]
              .filter((x) => x.v)
              .map((x) => (
                <div
                  key={x.k}
                  style={{
                    minHeight: 68,
                    padding: "10px 12px",
                    borderRadius: 14,
                    background: x.k === "Presentation"
                      ? `linear-gradient(135deg, ${T.brand}18 0%, rgba(10,22,47,0.98) 100%)`
                      : x.k === "Topic"
                        ? `linear-gradient(135deg, ${T.review}18 0%, rgba(10,22,47,0.98) 100%)`
                        : `linear-gradient(135deg, ${T.submission}18 0%, rgba(10,22,47,0.98) 100%)`,
                    border: `1.5px solid ${T.borderMedium}`,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03)",
                  }}
                >
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.14em", color: T.textMuted, textTransform: "uppercase", fontFamily: T.fontFamily }}>{x.k}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, fontFamily: T.fontFamily, lineHeight: 1.2 }}>{x.v}</span>
                </div>
              ))}
          </div>
          {topicAmbiguityDetected && (
            <div
              style={{
                marginTop: 10,
                padding: "8px 10px",
                borderRadius: 10,
                border: `1px solid ${T.gold}55`,
                background: `linear-gradient(180deg, ${T.surface1} 0%, rgba(10,21,45,0.98) 100%)`,
                fontSize: 10,
                lineHeight: 1.45,
                color: "#D7E6FA",
                fontFamily: T.fontFamily,
              }}
            >
              {topicAmbiguityMessage}
            </div>
          )}
          {currentSubmissionReviewCount != null && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              <div style={{ padding: "8px 10px", borderRadius: 10, border: `1px solid ${T.borderMedium}`, background: T.surface1, minWidth: 108 }}>
                <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.textMuted, fontFamily: T.fontFamily }}>
                  Current reviews
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.textPrimary, fontFamily: T.fontFamily, lineHeight: 1.1, marginTop: 4 }}>
                  {currentSubmissionReviewCount}
                </div>
              </div>
            </div>
          )}
        </SectionShell>
      )}
    </div>
  );
}

function confTierToNumeric(tier) {
  return tier === "high" ? 88 : tier === "medium" ? 60 : tier === "low" ? 28 : 0;
}

function scoreToNumeric(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return 0;
  return Math.round((Math.min(Math.max(n, 0), 5) / 5) * 100);
}

function buildRecommendedRouteLabel({ specialist, aiRecommends }) {
  if (specialist) return "Specialist Review";
  if (aiRecommends === "Single review") return "Confirming Review";
  return "Full Review";
}

const MATRIX_FONT = {
  A: ["0110", "1001", "1111", "1001", "1001", "1001"],
  C: ["0111", "1000", "1000", "1000", "1000", "0111"],
  E: ["1111", "1000", "1110", "1000", "1000", "1111"],
  F: ["1111", "1000", "1110", "1000", "1000", "1000"],
  G: ["0111", "1000", "1011", "1001", "1001", "0111"],
  I: ["1111", "0010", "0010", "0010", "0010", "1111"],
  L: ["1000", "1000", "1000", "1000", "1000", "1111"],
  M: ["1001", "1111", "1111", "1001", "1001", "1001"],
  N: ["1001", "1101", "1101", "1011", "1011", "1001"],
  O: ["0110", "1001", "1001", "1001", "1001", "0110"],
  P: ["1110", "1001", "1110", "1000", "1000", "1000"],
  R: ["1110", "1001", "1110", "1010", "1001", "1001"],
  S: ["0111", "1000", "0110", "0001", "0001", "1110"],
  T: ["1111", "0010", "0010", "0010", "0010", "0010"],
  U: ["1001", "1001", "1001", "1001", "1001", "0110"],
  V: ["1001", "1001", "1001", "1001", "0110", "0110"],
  W: ["1001", "1001", "1001", "1111", "1111", "1001"],
  " ": ["000", "000", "000", "000", "000", "000"],
};

function buildMatrixCells(label) {
  const rows = 6;
  const gridCols = 104;
  const text = Array.from(String(label || "").toUpperCase());
  const patterns = text.map((char) => MATRIX_FONT[char] || MATRIX_FONT[" "]);
  const textCols = patterns.reduce((sum, pattern, index) => sum + pattern[0].length + (index < patterns.length - 1 ? 1 : 0), 0);
  const startCol = Math.max(0, Math.floor((gridCols - textCols) / 2));
  const lit = new Set();

  let cursor = startCol;
  patterns.forEach((pattern, patternIndex) => {
    const width = pattern[0].length;
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < width; col += 1) {
        if (pattern[row][col] === "1") {
          lit.add(`${row}-${cursor + col}`);
        }
      }
    }
    cursor += width;
    if (patternIndex < patterns.length - 1) cursor += 1;
  });

  return Array.from({ length: rows * gridCols }, (_, index) => {
    const row = Math.floor(index / gridCols);
    const col = index % gridCols;
    const key = `${row}-${col}`;
    const seed = (index * 37 + row * 17 + col * 13) % 100;
    return {
      key,
      lit: lit.has(key),
      delayMs: seed * 4.4,
      durationMs: 220 + (seed % 6) * 28,
    };
  });
}

function RecommendResolveField({ label, T, cycleStartedAt }) {
  const cells = useMemo(() => buildMatrixCells(label), [label]);
  const cellSize = 4;
  const cellGap = 1;
  const [started, setStarted] = useState(false);

  useEffect(() => {
    setStarted(false);
    const maxTail = cells.reduce((max, cell) => Math.max(max, cell.delayMs + cell.durationMs), 0);
    const kickAt = Math.max(0, AI_REVIEW_INTRO_TOTAL_MS - maxTail);
    const timer = window.setTimeout(() => setStarted(true), kickAt);
    return () => window.clearTimeout(timer);
  }, [cells, cycleStartedAt]);

  return (
    <div
      style={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        height: 38,
        borderRadius: 10,
        background: "transparent",
        border: "none",
        overflow: "hidden",
        display: "grid",
        gridTemplateColumns: `repeat(104, ${cellSize}px)`,
        gridTemplateRows: `repeat(6, ${cellSize}px)`,
        gap: cellGap,
        alignItems: "center",
        justifyContent: "center",
        padding: "5px 0 3px",
      }}
    >
      {cells.map((cell) => (
        <span
          key={cell.key}
          style={{
            display: "block",
            width: cellSize,
            height: cellSize,
            minHeight: cellSize,
            minWidth: cellSize,
            borderRadius: 1,
            background: cell.lit ? T.textPrimary : T.surface1,
            boxShadow: cell.lit ? `0 0 8px ${T.textPrimary}2e` : "none",
            animation: started
              ? (cell.lit
                ? `airp-outcome-lit ${cell.durationMs}ms ease-out ${cell.delayMs}ms both`
                : `airp-outcome-cell-fade ${cell.durationMs}ms ease-out ${cell.delayMs}ms both`)
              : "none",
            opacity: started ? undefined : 1,
          }}
        />
      ))}
    </div>
  );
}

export function AiReviewPopupOutcomeSection({ specialist, aiRecommends, cycleStartedAt, T }) {
  const [borderFlash, setBorderFlash] = useState(false);

  useEffect(() => {
    setBorderFlash(false);
    const timer = window.setTimeout(() => setBorderFlash(true), Math.max(0, AI_REVIEW_INTRO_TOTAL_MS - 140));
    return () => window.clearTimeout(timer);
  }, [cycleStartedAt]);

  if (!T) return null;

  const routeLabel = buildRecommendedRouteLabel({ specialist, aiRecommends });

  return (
    <div style={{ padding: "0 16px" }}>
      <div
        style={{
          minHeight: 62,
          borderRadius: 18,
          border: `1px solid ${borderFlash ? `${T.brandLight}B0` : T.borderMedium}`,
          background: `linear-gradient(180deg, rgba(8,14,29,0.94) 0%, rgba(7,12,24,0.98) 100%)`,
          boxShadow: borderFlash
            ? `inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px ${T.brandLight}24, 0 0 18px ${T.brandLight}20`
            : "inset 0 1px 0 rgba(255,255,255,0.02)",
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "5px 16px",
          overflow: "hidden",
          animation: borderFlash ? "airp-outcome-border-flash 620ms ease-out both" : "none",
          transition: "border-color 180ms ease, box-shadow 180ms ease",
        }}
      >
        <div
          style={{
            flex: "0 0 auto",
            minWidth: 138,
            alignSelf: "stretch",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "0 6px 0 2px",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.14em",
              color: "#8FA7C6",
              fontFamily: T.fontFamily,
            }}
          >
            AI Reccomends:
          </div>
        </div>

        <RecommendResolveField label={routeLabel} T={T} cycleStartedAt={cycleStartedAt} />
      </div>
    </div>
  );
}

export function AiReviewPopupSummarySection({
  confTier,
  score,
  T,
  onIntroComplete,
}) {
  if (!T) return null;

  const confNumeric = confTierToNumeric(confTier);
  const qualNumeric = scoreToNumeric(score);

  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 0 }}>
      <AiCompletionStrip
        T={T}
        confNumeric={confNumeric}
        qualNumeric={qualNumeric}
        onSequenceComplete={onIntroComplete}
      />
    </div>
  );
}
